import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import { adminApi, type FaqItem } from '@/api';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { NumberInput } from '@/components/ui/number-input';

type Form = {
  id?: number;
  question: string;
  answer: string;
  sortOrder: number;
  isVisible: boolean;
};

const empty = (): Form => ({ question: '', answer: '', sortOrder: 0, isVisible: true });

export function AdminFaq() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ['admin', 'faq'], queryFn: adminApi.faq.list });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<Form>(empty());

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['admin', 'faq'] });
    qc.invalidateQueries({ queryKey: ['faq'] });
  };

  const save = useMutation({
    mutationFn: () =>
      form.id
        ? adminApi.faq.update(form.id, form)
        : adminApi.faq.create(form),
    onSuccess: () => {
      toast.success('Сохранено');
      invalidate();
      setOpen(false);
    },
    onError: () => toast.error('Не удалось сохранить'),
  });

  const remove = useMutation({
    mutationFn: (id: number) => adminApi.faq.remove(id),
    onSuccess: () => {
      toast.success('Удалено');
      invalidate();
    },
  });

  function openEdit(f: FaqItem) {
    setForm({
      id: f.id,
      question: f.question ?? '',
      answer: f.answer ?? '',
      sortOrder: f.sortOrder ?? 0,
      isVisible: f.isVisible ?? true,
    });
    setOpen(true);
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-extrabold text-eol-white">FAQ</h1>
        <Button onClick={() => { setForm(empty()); setOpen(true); }} className="gap-2">
          <Plus className="h-4 w-4" /> Добавить вопрос
        </Button>
      </div>

      {!data?.length ? (
        <p className="text-eol-desc">Пока пусто.</p>
      ) : (
        <div className="space-y-3">
          {data.map((f) => (
            <Card key={f.id}>
              <CardContent className="pt-4 pb-4 flex items-start justify-between gap-3 flex-wrap">
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-eol-white">{f.question}</p>
                  <p className="text-sm text-eol-desc mt-1 line-clamp-2">{f.answer}</p>
                  {!f.isVisible && <Badge variant="danger" className="mt-2">Скрыт</Badge>}
                </div>
                <div className="flex items-center gap-2">
                  <Button size="icon" variant="secondary" onClick={() => openEdit(f)} title="Редактировать">
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button
                    size="icon"
                    variant="outline"
                    onClick={() => { if (confirm('Удалить вопрос?')) remove.mutate(f.id); }}
                    title="Удалить"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{form.id ? 'Редактировать вопрос' : 'Новый вопрос'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <Input
              value={form.question}
              onChange={(e) => setForm({ ...form, question: e.target.value })}
              placeholder="Вопрос"
            />
            <textarea
              rows={6}
              className="w-full rounded-md border border-white/10 bg-eol-black/40 px-3 py-2 text-sm text-eol-white"
              value={form.answer}
              onChange={(e) => setForm({ ...form, answer: e.target.value })}
              placeholder="Ответ"
            />
            <div className="flex gap-3 items-center flex-wrap">
              <label className="flex flex-col gap-1">
                <span className="text-xs uppercase tracking-wider text-eol-desc">Порядок</span>
                <NumberInput value={form.sortOrder} onChange={(v) => setForm({ ...form, sortOrder: v })} step={10} className="w-28" />
              </label>
              <label className="flex items-center gap-2 text-eol-white mt-5">
                <input
                  type="checkbox"
                  checked={form.isVisible}
                  onChange={(e) => setForm({ ...form, isVisible: e.target.checked })}
                />
                Показывать
              </label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setOpen(false)}>Отмена</Button>
            <Button onClick={() => save.mutate()} disabled={save.isPending}>Сохранить</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
